import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, defer, tap, throwError } from 'rxjs';
import { CacheService } from '../services/cache.service';

// Matches the backend UserDto (BusinessLogic.Models): no first/last name (the
// user table has no such columns), roles are role NAMES. The list endpoint
// returns a bare array, not a paged envelope.
export interface UserListDto {
  id: number;
  companyId: number | null;
  emailAddress: string;
  userName: string | null;
  displayName: string | null;
  isPlatformStaff: boolean;
  canLogin: boolean;
  emailValidated: boolean;
  roles: string[];
}

export type UserDetailDto = UserListDto;

export interface CreateUserDto {
  emailAddress: string;
  userName?: string | null;
  displayName?: string | null;
  password: string;
  canLogin?: boolean;
}

export interface UpdateUserDto {
  userName?: string | null;
  displayName?: string | null;
  emailAddress: string;
  canLogin?: boolean;
}

export interface RoleListDto {
  id: number;
  name: string;
  description: string | null;
  permissionCount: number;
  isSystemDefault: boolean;
}

export interface RoleDetailDto {
  id: number;
  name: string;
  description: string | null;
  isSystemDefault: boolean;
  permissionKeys: string[];
}

export interface CreateRoleDto {
  name: string;
  description?: string | null;
  permissionKeys: string[];
}

export interface UpdateRoleDto {
  name: string;
  description?: string | null;
  permissionKeys: string[];
}

export interface PermissionDto {
  key: string;
  description: string;
  category: string;
}

// Per-user dynamic Agent Sign in with ChatGPT. The login response returns either
// the device URL + one-time code, or authenticated:true when already signed in.
export interface AgentLoginStart {
  url: string | null;
  code: string | null;
  expiresInSec: number;
  authenticated: boolean;
}

// Full connection state for the current user's dynamic Agent container.
export interface AgentStatus {
  problem?: import('../services/agent-problem').AgentProblem;
  enabled: boolean;
  authenticated: boolean | null;
  runtimeRunning?: boolean | null;
  detail: string | null;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
}

export interface UserListParams {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  sortDir?: string;
}

@Injectable({ providedIn: 'root' })
export class ConfigurationService {
  constructor(private http: HttpClient, private cache: CacheService) {}

  // Users — backend returns a bare array (no paging envelope).
  getUsers(): Observable<UserListDto[]> {
    return this.http.get<UserListDto[]>('/api/users');
  }

  createUser(dto: CreateUserDto): Observable<UserDetailDto> {
    return this.http.post<UserDetailDto>('/api/users', dto);
  }

  inviteUser(emailAddress: string): Observable<void> {
    return this.http.post<void>('/api/users/invite', { emailAddress });
  }

  sendPasswordReset(userId: number): Observable<void> {
    return this.http.post<void>(`/api/users/${userId}/password-reset`, {});
  }

  updateUser(id: number, dto: UpdateUserDto): Observable<UserDetailDto> {
    return this.http.put<UserDetailDto>(`/api/users/${id}`, dto);
  }

  deleteUser(id: number): Observable<void> {
    return this.http.delete<void>(`/api/users/${id}`);
  }

  assignRole(userId: number, roleId: number): Observable<void> {
    return this.http.post<void>(`/api/users/${userId}/roles`, { roleId });
  }

  removeRole(userId: number, roleId: number): Observable<void> {
    return this.http.delete<void>(`/api/users/${userId}/roles/${roleId}`);
  }

  // Roles
  getRoles(): Observable<RoleListDto[]> {
    return this.http.get<RoleListDto[]>('/api/roles');
  }

  getRoleById(id: number): Observable<RoleDetailDto> {
    return this.http.get<RoleDetailDto>(`/api/roles/${id}`);
  }

  createRole(dto: CreateRoleDto): Observable<RoleDetailDto> {
    return this.http.post<RoleDetailDto>('/api/roles', dto);
  }

  updateRole(id: number, dto: UpdateRoleDto): Observable<RoleDetailDto> {
    return this.http.put<RoleDetailDto>(`/api/roles/${id}`, dto);
  }

  deleteRole(id: number): Observable<void> {
    return this.http.delete<void>(`/api/roles/${id}`);
  }

  // Permissions
  getPermissions(): Observable<PermissionDto[]> {
    return this.http.get<PermissionDto[]>('/api/permissions');
  }

  // Generic config helpers (used by ConfigTableComponent wrappers)
  getConfigItems<T>(path: string): Observable<T[]> {
    return this.http.get<T[]>(path);
  }

  createConfigItem<T>(path: string, body: Partial<T>): Observable<T> {
    return this.http.post<T>(path, body);
  }

  updateConfigItem<T>(path: string, id: number, body: Partial<T>): Observable<T> {
    return this.http.put<T>(`${path}/${id}`, body);
  }

  deactivateConfigItem(path: string, id: number): Observable<void> {
    return this.http.delete<void>(`${path}/${id}`);
  }

  reorderConfigItems(path: string, order: { id: number; sortOrder: number }[]): Observable<void> {
    return this.http.put<void>(`${path}/sort-order`, order);
  }

  /** Last confirmed connection state for this signed-in user and tenant. */
  get cachedAgentStatus(): AgentStatus | undefined {
    return this.cache.get('agent-connection', 'status');
  }

  /** Always refresh; concurrent readers share the request without discarding the last result. */
  agentLoginStatus(): Observable<AgentStatus> {
    return this.cache.load('agent-connection', 'status', () =>
      this.http.get<AgentStatus>('/api/agent/login/status').pipe(
        tap(status => this.cache.set('agent-connection', 'status', status)),
        catchError(error => { this.cache.delete('agent-connection', 'status'); return throwError(() => error); }),
      ), { retain: false });
  }

  agentLoginStart(): Observable<AgentLoginStart> {
    return defer(() => {
      this.cache.clear('agent-connection');
      return this.cache.track(() => this.http.post<AgentLoginStart>('/api/agent/login/start', {})).pipe(
        tap(result => {
          this.cache.clear('agent-connection');
          this.cache.set<AgentStatus>('agent-connection', 'status', {
            enabled: true, authenticated: result.authenticated, detail: null,
          });
        }),
      );
    });
  }

  agentLogout(): Observable<void> {
    return defer(() => {
      this.cache.clear('agent-connection');
      return this.cache.track(() => this.http.post<void>('/api/agent/login/logout', {})).pipe(
        tap(() => {
          this.cache.clear('agent-connection');
          this.cache.set<AgentStatus>('agent-connection', 'status', {
            enabled: true, authenticated: false, detail: null,
          });
        }),
      );
    });
  }
}
